// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useState } from 'react';
import { Modal, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { useAppDispatch } from '../../../../store';
import { deleteJobs } from '../../../../store/interviewHostSlice';
import styles from './jobsRoles.module.scss';

interface DeleteJobsModalProps {
  open: boolean;
  onClose: () => void;
  jobIds: string[];
  jobTitles: { [key: string]: string };
  onSuccess?: () => void;
}

const DeleteJobsModal: React.FC<DeleteJobsModalProps> = ({ open, onClose, jobIds, jobTitles, onSuccess }) => {
  const dispatch = useAppDispatch();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async (): Promise<void> => {
    setDeleting(true);
    setError(null);
    try {
      await dispatch(deleteJobs({ jobIds })).unwrap();
      onClose();
      onSuccess?.();
    } catch (err: any) {
      console.error('Failed to delete job(s):', err);
      setError(err || 'Failed to delete job(s). Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = (): void => {
    if (!deleting) {
      onClose();
    }
  };

  const isSingleJob = jobIds.length === 1;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Delete {isSingleJob ? 'Job' : 'Jobs'}</h2>
          <button className={styles.modalClose} onClick={handleClose} disabled={deleting}>
            <CloseIcon />
          </button>
        </div>

        <div className={styles.modalBody}>
          {error && (
            <div className={styles.errorMessage}>
              <ErrorIcon className={styles.errorIcon} />
              <p>{error}</p>
            </div>
          )}

          <div className={styles.warningMessage}>
            <WarningIcon className={styles.warningIcon} />
            <p>
              Are you sure you want to delete <strong>{jobIds.length}</strong> {isSingleJob ? 'job' : 'jobs'}? This
              action cannot be undone.
            </p>
          </div>

          <div className={styles.jobsList}>
            <p className={styles.jobsListTitle}>{isSingleJob ? 'Job to be deleted:' : 'Jobs to be deleted:'}</p>
            <ul className={styles.jobsListItems}>
              {jobIds.map((jobId) => (
                <li key={jobId} className={styles.jobsListItem}>
                  <span className={styles.jobId}>{jobId}</span>
                  <span className={styles.jobTitle}>{jobTitles[jobId] || 'Unknown Job'}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className={styles.confirmHint}>
            All candidates and related data for {isSingleJob ? 'this job' : 'these jobs'} will be permanently removed.
          </p>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={handleClose} disabled={deleting}>
            Cancel
          </button>
          <button className={styles.btnDelete} onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : `Delete ${isSingleJob ? 'Job' : `${jobIds.length} Jobs`}`}
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default DeleteJobsModal;
